import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/authService';

interface User {
  id: string;
  name: string;
  email: string;
  roles: string[];
  isAdmin: boolean;
  lastLogin: string;
}

interface AuthContextData {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Vérifie si l'utilisateur est déjà connecté
    const loadUserFromStorage = async () => {
      try {
        const { token, user: storedUser } = authService.getStoredAuthData();

        if (token && storedUser) {
          setUser(storedUser as User);
        }
      } catch (error) {
        console.error('Erreur lors de la lecture du storage:', error);
        // En cas d'erreur, on nettoie le storage corrompu
        authService.clearAuthData();
      }

      setLoading(false);
    };

    loadUserFromStorage();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);

      const response = await authService.login({ email, password });
      const { token, user: userData } = response;

      if (!token || !userData) {
        throw new Error('Réponse invalide du serveur');
      }

      authService.storeAuthData(token, userData);
      setUser(userData as User);
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      throw new Error(error?.response?.data?.message || error?.message || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);

      // Appel à l'API pour se déconnecter
      await authService.logout();

      // Suppression des données stockées
      authService.clearAuthData();

      setUser(null);
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        loading,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}; 